import type { ComponentPropsWithoutRef } from "react";
import { LoaderCircle } from "lucide-react";

import { cn } from "../../lib/utils";

export type LoadingSpinnerSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl";

export interface LoadingSpinnerProps
  extends ComponentPropsWithoutRef<typeof LoaderCircle> {
  size?: LoadingSpinnerSize;
  label?: string;
}

const DEFAULT_SPINNER_SIZE: LoadingSpinnerSize = "xl";

const spinnerSizeClass: Record<LoadingSpinnerSize, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
  xl: "h-6 w-6",
  "2xl": "h-7 w-7",
  "3xl": "h-8 w-8",
};

export function LoadingSpinner({
  size = DEFAULT_SPINNER_SIZE,
  label,
  className,
  ...props
}: LoadingSpinnerProps) {
  return (
    <LoaderCircle
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "status" : undefined}
      className={cn(
        "shrink-0 animate-spin",
        size === DEFAULT_SPINNER_SIZE && "text-muted-foreground",
        spinnerSizeClass[size],
        className
      )}
      {...props}
    />
  );
}

export interface LoadingStateProps {
  label?: string;
  showLabel?: boolean;
  spinnerSize?: LoadingSpinnerSize;
  className?: string;
}

export function LoadingState({
  label = "กำลังโหลด...",
  showLabel = false,
  spinnerSize = DEFAULT_SPINNER_SIZE,
  className,
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex min-h-[120px] flex-col items-center justify-center gap-2 py-8 text-muted-foreground",
        className
      )}
    >
      <LoadingSpinner size={spinnerSize} />
      {showLabel ? (
        <p className="text-sm">{label}</p>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </div>
  );
}

export interface TableLoadingStateProps {
  label?: string;
  showLabel?: boolean;
  spinnerSize?: LoadingSpinnerSize;
  className?: string;
}

export function TableLoadingState({
  label = "กำลังโหลด...",
  showLabel = true,
  spinnerSize = DEFAULT_SPINNER_SIZE,
  className,
}: TableLoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex h-full min-h-[240px] flex-1 items-center justify-center text-muted-foreground",
        className
      )}
    >
      <LoadingSpinner size={spinnerSize} />
      {showLabel ? (
        <span className="ml-2 text-[13px]">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </div>
  );
}
